import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as mapboxgl from 'mapbox-gl';
import { take } from 'rxjs/operators';
import { BuildingService } from 'src/app/geometrics-form/buildings/state/building.service';
import { GeometricFormTypeEnum } from 'src/app/geometrics-form/state/geometric-form.type';
import { VertexService } from 'src/app/geometrics-form/vertexes/state/vertex.service';
import { featureToSvg } from '../../mappers/featureToSvg';
import { FeatureSelectionService } from './feature-selection.service';
import { LayerAction } from './layer-action';

@Injectable({
  providedIn: 'root'
})
export class FeatureDragingDropService extends LayerAction {

  private mapbox: mapboxgl.Map;
  private canvas: HTMLCanvasElement;
  private marker: mapboxgl.Marker;

  private selected: mapboxgl.MapboxGeoJSONFeature;
  private hover: mapboxgl.MapboxGeoJSONFeature;
  private touched: { [id: number]: mapboxgl.MapboxGeoJSONFeature } = {};
  private grabbed: mapboxgl.MapboxGeoJSONFeature;

  constructor(
    private snackBar: MatSnackBar,
    private buildingService: BuildingService,
    private vertexService: VertexService,
    private selectionService: FeatureSelectionService
  ) {
    super();
  }

  listen(mapbox: mapboxgl.Map, layerName: string) {
    this.mapbox = mapbox;
    this.canvas = mapbox.getCanvas();

    this.listenSelection();
    this.listenHover(layerName);
  }

  private listenSelection() {

    this.selectionService.selecteds$.subscribe(featuresMap => {

      if (featuresMap.size === 1) {
        const feature = Array.from(featuresMap.values())[0] as mapboxgl.MapboxGeoJSONFeature;
        this.selected = feature;
        this.hover = feature;
        this.cursor('move');
        return;
      }

      this.selected = null;
      this.cursor('');
    });
  }

  private listenHover(layerName: string) {

    this.mapbox.on('mouseenter', layerName, (e) => {

      if (!this.selected) {
        return;
      }

      if (e.defaultPrevented || e.originalEvent.ctrlKey) {
        return;
      }

      if (!e.features || !e.features.length) {
        return;
      }

      e.features.forEach(f => this.touched[f.properties.id] = f);
      const nearest = this.findNearest(e.lngLat, e.features);
      if (nearest && nearest.id === this.selected.id) {

        this.hover = nearest;
        this.cursor('move');
      }
    });

    this.mapbox.on('mousedown', layerName, (e) => {

      if (!this.selected) {
        return;
      }

      if (e.defaultPrevented || e.originalEvent.ctrlKey) {
        return;
      }

      if (!this.hover) {
        return;
      }

      this.cursor('grab');
      this.grabbed = this.hover;

      // Prevent the default map drag behavior.
      e.preventDefault();

      const moveFn = (moveEvent) => {
        const coords = moveEvent.lngLat;

        if (this.marker) {
          this.marker.setLngLat(coords);

        } else if (this.mouveDistance(moveEvent, this.grabbed) > 5) {
          this.createMarker(this.grabbed, coords);
          this.cursor('grabbing');
          this.setState(this.grabbed, 'm_dragin');
        }

      };

      this.mapbox.on('mousemove', moveFn);
      this.mapbox.once('mouseup', (mouseupEvent) => {
        this.cursor('');

        if (this.grabbed) {
          this.move(this.grabbed, mouseupEvent.lngLat);
          this.grabbed = null;
        }

        this.mapbox.off('mousemove', moveFn);
      });

    });

    this.mapbox.on('mouseleave', layerName, (e) => {
      this.hover = null;
      this.cursor('');
    });
  }

  private mouveDistance(e: any, selected: mapboxgl.MapboxGeoJSONFeature) {
    return e.lngLat.distanceTo(this.getLngLat(selected));
  }

  protected findNearest(coord: mapboxgl.LngLat, features: mapboxgl.MapboxGeoJSONFeature[]): mapboxgl.MapboxGeoJSONFeature {

    return features.reduce((previous, current) => {

      if (previous && this.isNearest(coord, previous, current)) {
        return previous;
      }
      return current;
    }, null);

  }

  protected isNearest(coord: mapboxgl.LngLat, featureA: mapboxgl.MapboxGeoJSONFeature, featureB: mapboxgl.MapboxGeoJSONFeature): boolean {
    const lngLatA = this.getLngLat(featureA);
    const lngLatB = this.getLngLat(featureB);
    return coord.distanceTo(lngLatA) < coord.distanceTo(lngLatB);
  }

  protected getLngLat(feature: mapboxgl.MapboxGeoJSONFeature) {
    return mapboxgl.LngLat.convert([feature.properties.longitude, feature.properties.latitude]);
  }

  protected setState(feature: mapboxgl.MapboxGeoJSONFeature, key: string) {
    const state = { [key]: true };
    this.mapbox.setFeatureState(feature, state);
  }

  protected cleanState(features: mapboxgl.MapboxGeoJSONFeature[], key: string) {
    features.forEach(feature => {
      // Necessary for https://github.com/mapbox/mapbox-gl-js/issues/9461
      const existing = this.mapbox.getFeatureState(feature);
      if (existing.hasOwnProperty(key)) {
        this.mapbox.removeFeatureState(feature, key);
      }
    });
  }

  protected cursor(cursor: string) {
    this.canvas.style.cursor = cursor;
  }


  private createMarker(feature, coords: mapboxgl.LngLat) {
    const el = document.createElement('div');
    el.innerHTML = this.createSvg(feature);

    this.marker = new mapboxgl.Marker(el)
      .setLngLat(coords)
      .addTo(this.mapbox);
  }

  private createSvg(feature) {
    return featureToSvg(feature);
  }

  // TODO Create observable to listen actions
  private move(feature: mapboxgl.MapboxGeoJSONFeature, coords: mapboxgl.LngLat) {
    const snack = this.snackBar.open(`Updating element, wait...`);

    const id = feature.properties.id;

    let service;

    if (this.isBuilding(feature)) {
      service = this.buildingService.move(id, coords.lat, coords.lng);

    } else if (this.isVertex(feature)) {
      service = this.vertexService.move(id, coords.lat, coords.lng);
    }
    service
      .pipe(take(1))
      .subscribe(() => {
        snack.dismiss();

        this.cleanState([feature], DRAGIN_STATE);

        if (this.marker) {
          this.marker.remove();
          this.marker = null;
        }
      });
  }

  private isVertex(f: mapboxgl.MapboxGeoJSONFeature) {
    return f.layer.id === GeometricFormTypeEnum.VERTEX;
  }

  private isBuilding(f: mapboxgl.MapboxGeoJSONFeature) {
    return f.layer.id === GeometricFormTypeEnum.BUILDING;
  }
}

const DRAGIN_STATE = 'm_dragin';
